import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, MapPin, ShieldCheck, BedDouble, Car, Headset, Plane, Signpost, CalendarDays } from "lucide-react";
import { HERO_FILL } from "@/data/heroFill";
import { useLang } from "@/i18n/LanguageContext";

const HIGHLIGHTS = {
  "Dental World Progreso": [
    { icon: Signpost, key: "First after the bridge" },
    { icon: CalendarDays, key: "Open Sundays" },
  ],
};

const PROXIMITY = {
  "Mexicali": "border", "Tijuana": "border", "Los Algodones": "border", "Nuevo Progreso": "border",
  "Cancún": "air", "Bogotá": "air", "San José": "air", "Istanbul": "air",
};

const FEATURES = [
  { icon: BedDouble, key: "hotel" },
  { icon: Car, key: "shuttle" },
  { icon: Headset, key: "concierge" },
];

export default function ClinicCard({ clinic, index = 0 }) {
  const { t } = useLang();
  const fill = HERO_FILL[clinic.name];
  const proxType = PROXIMITY[clinic.city];
  return (
    <Link to={`/clinics/${clinic.id}`} data-testid={`clinic-card-${clinic.id}`}
      className="group block animate-fade-up" style={{ animationDelay: `${index * 70}ms` }}>
      <Card className="overflow-hidden rounded-2xl border-border shadow-sm transition-all duration-200 group-hover:-translate-y-1 group-hover:shadow-lg h-full flex flex-col">
        <div className={`relative aspect-[4/3] overflow-hidden ${fill ? fill.bg : "bg-secondary/10"}`}>
          {fill ? (
            <>
              {fill.blur && <img src={clinic.hero_image} aria-hidden="true" alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50" />}
              <img src={clinic.hero_image} alt={clinic.name} className="relative w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" />
            </>
          ) : (
            <img src={clinic.hero_image} alt={clinic.name} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" />
          )}
          <div className="absolute top-3 left-3 flex items-center gap-1 rounded-full bg-white/90 backdrop-blur px-2.5 py-1 text-xs font-semibold">
            <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" /> {clinic.rating} <span className="text-muted-foreground">({clinic.reviews_count})</span>
          </div>
          {clinic.featured && <Badge className="absolute top-3 right-3 bg-accent hover:bg-accent">{t("card.featured")}</Badge>}
        </div>
        <div className="p-5 flex flex-col flex-1">
          <h3 className="font-heading font-bold text-lg leading-tight">{clinic.name}</h3>
          <p className="flex items-center gap-1 text-sm text-muted-foreground mt-1"><MapPin className="w-3.5 h-3.5" /> {clinic.city}, {clinic.country}</p>
          {proxType && (
            <p className="inline-flex items-center gap-1 text-xs font-semibold text-primary mt-1.5" data-testid="card-proximity">
              {proxType === "air" ? <Plane className="w-3.5 h-3.5 shrink-0" /> : <MapPin className="w-3.5 h-3.5 shrink-0" />}
              {t(`card.proximity.${clinic.city}`)}
            </p>
          )}
          {HIGHLIGHTS[clinic.name] && (
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="card-highlights">
              {HIGHLIGHTS[clinic.name].map((h) => (
                <span key={h.key} className="inline-flex items-center gap-1 text-xs font-semibold rounded-full bg-accent/10 text-accent px-2 py-0.5">
                  <h.icon className="w-3 h-3 shrink-0" /> {t(`card.highlights.${h.key}`)}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-3">
            {clinic.certifications?.slice(0, 3).map((c) => (
              <span key={c} className="inline-flex items-center gap-1 text-xs bg-secondary/5 text-secondary rounded-full px-2 py-0.5"><ShieldCheck className="w-3 h-3" /> {c}</span>
            ))}
          </div>
          <div className="mt-auto pt-4 flex items-center justify-between border-t border-border mt-4">
            <div className="flex items-center gap-3">
              {FEATURES.map((f) => (
                <span key={f.key} className="inline-flex items-center gap-1 text-xs text-muted-foreground" title={t(`card.${f.key}`)}>
                  <f.icon className="w-3.5 h-3.5 text-primary" /> {t(`card.${f.key}`)}
                </span>
              ))}
            </div>
          </div>
          <span className="text-sm font-medium text-primary group-hover:underline mt-3">{t("common.viewClinic")}</span>
        </div>
      </Card>
    </Link>
  );
}
